<script setup lang="ts">
// Estilos para animaciones
const style = document.createElement('style');
style.textContent = `
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
document.head.appendChild(style);
import LoadingModal from '@/components/LoadingModal.vue';
import useUserLog from '@/internal/modules/users/composables/useUserLog';
import type { User } from '@/modules/users/interfaces/user';
import { formatDate } from '@/utils';
import { Pagination } from '@/interfaces/paginatedResponse';
import { 
  ClockIcon, 
  DocumentTextIcon,
  UserCircleIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  TrashIcon,
  PlusCircleIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
  FunnelIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon
} from '@heroicons/vue/24/outline';
import { ref, computed, Ref, ComputedRef } from "vue";

// Definición de la interfaz para los logs
interface LogProperties {
  old?: Record<string, any>;
  attributes?: Record<string, any>;
  isInternal?: boolean;
}

interface UserLog {
  logName: string;
  description: string;
  event: string;
  subjectId: number;
  subjectType: string;
  causerId: number | null;
  causerType: string | null;
  properties: LogProperties;
  createdAt: string;
}

interface Props {
  user: User;
}

const props = defineProps<Props>();

// Función para exportar logs a CSV
const exportToCSV = () => {
  if (filteredLogs.value.length === 0) return;
  
  // Encabezados del CSV
  const headers = ['Fecha', 'Evento', 'Descripción', 'Valores Anteriores', 'Valores Nuevos'];
  
  // Datos formateados para CSV
  const csvData = filteredLogs.value.map((log: UserLog) => {
    // Formatear valores anteriores y nuevos
    const oldValues = log.properties?.old ? 
      Object.entries(log.properties.old)
        .map(([key, value]) => `${getPrettyPropertyName(key)}: ${formatValue(value)}`)
        .join('; ') : '';
    
    const newValues = log.properties?.attributes ? 
      Object.entries(log.properties.attributes)
        .map(([key, value]) => `${getPrettyPropertyName(key)}: ${formatValue(value)}`)
        .join('; ') : '';
    
    return [
      formatDate(log.createdAt),
      getEventName(log.event),
      log.description,
      oldValues,
      newValues
    ];
  });
  
  // Combinar encabezados y datos
  const csvContent = [
    headers.join(','),
    ...csvData.map((row: any[]) => row.map((cell: any) => `"${cell?.toString().replace(/"/g, '""') || ''}"`).join(','))
  ].join('\n');
  
  // Crear blob y descargar
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `registro-cambios-${props.user.name}-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


const userLogData = useUserLog({
  params: {
    id: props.user.id,
  },
  immediate: true,
});
const { logs, isLoading, pagination, setPage, refetch, page } = userLogData as {
  logs: Ref<UserLog[]>;
  isLoading: Ref<boolean>;
  pagination: Ref<Pagination | undefined>;
  setPage: (page: number | string) => void;
  refetch: () => void;
  page: Ref<number>;
};

// Función para obtener el icono según el tipo de evento
const getEventIcon = (event: string) => {
  switch (event) {
    case 'created':
      return PlusCircleIcon;
    case 'updated':
      return PencilSquareIcon;
    case 'deleted':
      return TrashIcon;
    default:
      return DocumentDuplicateIcon;
  }
};

// Función para obtener el color según el tipo de evento
const getEventColor = (event: string) => {
  switch (event) {
    case 'created':
      return 'text-green-500';
    case 'updated':
      return 'text-blue-500';
    case 'deleted':
      return 'text-red-500';
    default:
      return 'text-gray-500';
  }
};

// Función para formatear los valores de los cambios
const formatValue = (value: any) => {
  if (value === null || value === undefined) return 'No definido';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (value instanceof Date) return formatDate(value.toString());
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// Estado para el log seleccionado para ver detalles
const selectedLog = ref<UserLog | null>(null);

// Función para mostrar detalles de un log
const showLogDetails = (log: UserLog) => {
  selectedLog.value = selectedLog.value === log ? null : log;
};

// Limpiar la selección del log
const clearSelectedLog = () => {
  selectedLog.value = null;
};

// Estado para expandir todos los logs
const expandAllLogs = ref(false);

// Función para expandir o contraer todos los logs
const toggleExpandAllLogs = () => {
  expandAllLogs.value = !expandAllLogs.value;
  if (expandAllLogs.value) {
    // Si expandimos todos, seleccionamos el primer log con cambios
    const logWithChanges = filteredLogs.value.find((log: UserLog) => hasChanges(log));
    if (logWithChanges) {
      selectedLog.value = logWithChanges;
    }
  } else {
    // Si contraemos todos, limpiamos la selección
    selectedLog.value = null;
  }
};

// Verificar si un log tiene cambios para mostrar
const hasChanges = (log: UserLog) => {
  return (log.properties?.old && Object.keys(log.properties.old).length > 0) || 
         (log.properties?.attributes && Object.keys(log.properties.attributes).length > 0);
};

// Función para obtener un nombre más amigable para las propiedades
const getPrettyPropertyName = (key: string | number) => {
  const propertyNames: Record<string, string> = {
    available: 'Disponible',
    name: 'Nombre',
    email: 'Correo electrónico',
    phone: 'Teléfono',
    status: 'Estatus',
    role_id: 'Rol',
    company_id: 'Empresa',
    created_at: 'Fecha de creación',
    updated_at: 'Fecha de actualización',
    deleted_at: 'Fecha de eliminación',
    password: 'Contraseña',
    remember_token: 'Token de recordatorio',
    // Agregar más mapeos según sea necesario
  };
  
  const keyStr = String(key);
  return propertyNames[keyStr] || keyStr;
};

// Estado para filtros y búsqueda
const searchTerm = ref('');
const selectedEventType = ref('all');
const showFilters = ref(false);
const dateFrom = ref('');
const dateTo = ref('');
const sortOrder = ref('desc'); // 'desc' para más recientes primero, 'asc' para más antiguos primero

// Función para cambiar el orden de los logs
const toggleSortOrder = () => {
  sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc';
};

// Función para validar si una fecha está dentro del rango seleccionado
const isDateInRange = (dateStr: string) => {
  if (!dateFrom.value && !dateTo.value) return true;
  
  const date = new Date(dateStr);
  let isValid = true;
  
  if (dateFrom.value) {
    const fromDate = new Date(dateFrom.value);
    fromDate.setHours(0, 0, 0, 0);
    isValid = isValid && date >= fromDate;
  }
  
  if (dateTo.value) {
    const toDate = new Date(dateTo.value);
    toDate.setHours(23, 59, 59, 999);
    isValid = isValid && date <= toDate;
  }
  
  return isValid;
};

// Verificar si hay filtros activos
const hasActiveFilters = computed(() => {
  return searchTerm.value !== '' || 
         selectedEventType.value !== 'all' || 
         dateFrom.value !== '' || 
         dateTo.value !== '';
});

// Filtrar logs según los criterios
const filteredLogs: ComputedRef<UserLog[]> = computed(() => {
  if (!logs.value || !Array.isArray(logs.value)) return [];
  
  // Primero filtramos los logs
  const filtered = logs.value.filter((log: UserLog) => {
    // Filtrar por tipo de evento
    if (selectedEventType.value !== 'all' && log.event !== selectedEventType.value) {
      return false;
    }
    
    // Filtrar por rango de fechas
    if (!isDateInRange(log.createdAt)) {
      return false;
    }
    
    // Filtrar por término de búsqueda
    if (searchTerm.value) {
      const searchLower = searchTerm.value.toLowerCase();
      const descriptionMatch = log.description.toLowerCase().includes(searchLower);
      
      // Buscar en propiedades antiguas
      let oldPropsMatch = false;
      if (log.properties && log.properties.old) {
        oldPropsMatch = Object.entries(log.properties.old).some(([key, value]) => {
          const valueStr = String(value).toLowerCase();
          return key.toLowerCase().includes(searchLower) || valueStr.includes(searchLower);
        });
      }
      
      // Buscar en propiedades nuevas
      let newPropsMatch = false;
      if (log.properties && log.properties.attributes) {
        newPropsMatch = Object.entries(log.properties.attributes).some(([key, value]) => {
          const valueStr = String(value).toLowerCase();
          return key.toLowerCase().includes(searchLower) || valueStr.includes(searchLower);
        });
      }
      
      return descriptionMatch || oldPropsMatch || newPropsMatch;
    }
    
    return true;
  });
  
  // Luego ordenamos los resultados filtrados según sortOrder
  return filtered.sort((a: UserLog, b: UserLog) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    
    return sortOrder.value === 'desc' ? Number(dateB) - Number(dateA) : Number(dateA) - Number(dateB);
  });
});

// Función para limpiar filtros
const clearFilters = () => {
  searchTerm.value = '';
  selectedEventType.value = 'all';
  dateFrom.value = '';
  dateTo.value = '';
};

// Función para obtener el nombre del evento en español
const getEventName = (event: string) => {
  switch (event) {
    case 'created': return 'Creación';
    case 'updated': return 'Actualización';
    case 'deleted': return 'Eliminación';
    default: return 'Otro';
  }
};
</script>

<template>
  <div class="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
    <div class="bg-white rounded-lg shadow-sm overflow-hidden">
      <!-- Encabezado -->
      <div class="px-6 py-5 border-b border-gray-200 bg-indigo-50">
        <div class="flex justify-between items-center">
          <div>
            <h3 class="text-lg leading-6 font-bold text-gray-900 flex items-center">
              <DocumentTextIcon class="h-5 w-5 text-indigo-500 mr-2" />
              Registro de cambios
            </h3>
            <p class="mt-1 max-w-2xl text-xs text-gray-500 uppercase tracking-wide">Historial de modificaciones realizadas</p>
          </div>
          <div class="flex space-x-2">
            <button 
              @click="exportToCSV" 
              :disabled="filteredLogs.length === 0"
              class="inline-flex items-center px-3 py-1.5 border border-green-300 text-xs font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon class="h-4 w-4 mr-1" />
              Exportar CSV
            </button>

            <button
              @click="toggleExpandAllLogs"
              :disabled="filteredLogs.length === 0"
              class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DocumentDuplicateIcon class="h-4 w-4 mr-1" />
              {{ expandAllLogs ? 'Contraer todos' : 'Expandir todos' }}
            </button>
            <button 
              disabled
              class="inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 opacity-50 cursor-not-allowed"
              :class="hasActiveFilters ? 'border-indigo-500 text-indigo-700 bg-indigo-50' : 'border-indigo-300 text-indigo-700 bg-indigo-50'"
            >
              <FunnelIcon class="h-4 w-4 mr-1" />
              Filtros
              <ChevronDownIcon class="h-4 w-4 ml-1" :class="{'transform rotate-180': showFilters}" />
              <span v-if="hasActiveFilters" class="ml-1.5 flex h-2 w-2 relative">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
              </span>
            </button>
          </div>
        </div>
        
        <!-- Filtros -->
        <div v-if="showFilters" class="mt-4 pt-4 border-t border-indigo-200">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Búsqueda -->
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon class="h-4 w-4 text-gray-400" />
              </div>
              <input
                v-model="searchTerm"
                type="text"
                placeholder="Buscar en los registros..."
                class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            
            <!-- Filtro por tipo de evento -->
            <div>
              <select
                v-model="selectedEventType"
                class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">Todos los eventos</option>
                <option value="created">Creación</option>
                <option value="updated">Actualización</option>
                <option value="deleted">Eliminación</option>
              </select>
            </div>
          </div>
          
          <!-- Filtro por rango de fechas -->
          <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="date-from" class="block text-xs font-medium text-gray-700 mb-1">Fecha desde</label>
              <input
                type="date"
                id="date-from"
                v-model="dateFrom"
                class="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label for="date-to" class="block text-xs font-medium text-gray-700 mb-1">Fecha hasta</label>
              <input
                type="date"
                id="date-to"
                v-model="dateTo"
                class="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>
          
          <!-- Botones de acción -->
          <div class="mt-3 flex justify-end">
            <button
              @click="clearFilters"
              class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      <!-- Contenido -->
      <div class="p-6">
        <!-- Leyenda de eventos -->
        <div class="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div class="flex justify-between items-center mb-2">
            <h4 class="text-sm font-medium text-gray-700 flex items-center">
              <InformationCircleIcon class="h-4 w-4 text-indigo-500 mr-1" />
              Tipos de eventos
            </h4>
            <span class="text-xs text-gray-500">{{ filteredLogs.length }} {{ filteredLogs.length === 1 ? 'resultado' : 'resultados' }}</span>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div class="flex items-center">
              <div class="rounded-full p-1 bg-green-100 mr-2">
                <PlusCircleIcon class="h-4 w-4 text-green-500" />
              </div>
              <span class="text-xs text-gray-600">Creación</span>
            </div>
            <div class="flex items-center">
              <div class="rounded-full p-1 bg-blue-100 mr-2">
                <PencilSquareIcon class="h-4 w-4 text-blue-500" />
              </div>
              <span class="text-xs text-gray-600">Actualización</span>
            </div>
            <div class="flex items-center">
              <div class="rounded-full p-1 bg-red-100 mr-2">
                <TrashIcon class="h-4 w-4 text-red-500" />
              </div>
              <span class="text-xs text-gray-600">Eliminación</span>
            </div>
            <div class="flex items-center">
              <div class="rounded-full p-1 bg-gray-100 mr-2">
                <DocumentDuplicateIcon class="h-4 w-4 text-gray-500" />
              </div>
              <span class="text-xs text-gray-600">Otro</span>
            </div>
          </div>
        </div>
        
        <!-- Sección de registros -->
        <div v-if="filteredLogs.length > 0" class="space-y-6">
          <div v-for="(log, index) in filteredLogs" :key="index" 
            class="bg-white rounded-lg border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
            @click="showLogDetails(log)">
            <!-- Encabezado del log -->
            <div class="flex items-center justify-between border-b pb-3" :class="{'mb-3': selectedLog === log || !hasChanges(log)}">
              <div class="flex items-center">
                <div :class="['rounded-full p-1.5', log.event === 'created' ? 'bg-green-100' : log.event === 'updated' ? 'bg-blue-100' : log.event === 'deleted' ? 'bg-red-100' : 'bg-gray-100']">
                  <component :is="getEventIcon(log.event)" :class="['h-5 w-5', getEventColor(log.event)]" />
                </div>
                <div class="ml-3">
                  <h4 class="text-md font-bold text-gray-900">{{ log.description }}</h4>
                  <div class="flex items-center space-x-2">
                    <span class="text-xs font-medium px-2 py-0.5 rounded-full" :class="{
                      'bg-green-100 text-green-800': log.event === 'created',
                      'bg-blue-100 text-blue-800': log.event === 'updated',
                      'bg-red-100 text-red-800': log.event === 'deleted',
                      'bg-gray-100 text-gray-800': !['created', 'updated', 'deleted'].includes(log.event)
                    }">{{ getEventName(log.event) }}</span>
                    <span v-if="hasChanges(log)" class="text-xs text-indigo-600 flex items-center">
                      <InformationCircleIcon class="h-3 w-3 mr-1" />
                      {{ selectedLog === log ? 'Ocultar detalles' : 'Ver detalles' }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="flex items-center text-sm text-gray-500">
                <ClockIcon class="h-4 w-4 mr-1 text-indigo-500" />
                {{ formatDate(log.createdAt) }}
              </div>
            </div>

            <!-- Detalles del cambio (expandible) -->
            <div v-if="selectedLog === log && hasChanges(log)" class="mt-3 animate-fadeIn">
              <div class="flex justify-between items-start mb-2">
                <h5 class="text-xs uppercase font-semibold text-gray-700 tracking-wider">Detalles del cambio</h5>
                <button @click="clearSelectedLog" class="text-gray-400 hover:text-gray-600 focus:outline-none">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Valores anteriores -->
                <div v-if="log.properties && log.properties.old && Object.keys(log.properties.old).length > 0" class="bg-red-50 p-3 rounded-md">
                  <h5 class="text-xs uppercase font-semibold text-red-700 tracking-wider mb-2">Valores anteriores</h5>
                  <div v-for="(value, key) in log.properties.old" :key="key" class="flex items-start py-1">
                    <div class="w-full">
                      <span class="text-xs font-medium text-gray-700 block">{{ getPrettyPropertyName(key) }}</span>
                      <span class="text-sm text-gray-900">{{ formatValue(value) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Valores nuevos -->
                <div v-if="log.properties && log.properties.attributes && Object.keys(log.properties.attributes).length > 0" class="bg-green-50 p-3 rounded-md">
                  <h5 class="text-xs uppercase font-semibold text-green-700 tracking-wider mb-2">Valores nuevos</h5>
                  <div v-for="(value, key) in log.properties.attributes" :key="key" class="flex items-start py-1">
                    <div class="w-full">
                      <span class="text-xs font-medium text-gray-700 block">{{ getPrettyPropertyName(key) }}</span>
                      <span class="text-sm text-gray-900">{{ formatValue(value) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mensaje cuando no hay logs después de filtrar -->
        <div v-else-if="logs && logs.length > 0 && filteredLogs.length === 0" class="text-center py-10">
          <FunnelIcon class="mx-auto h-12 w-12 text-gray-400" />
          <h3 class="mt-2 text-sm font-medium text-gray-900">No hay resultados para los filtros aplicados</h3>
          <p class="mt-1 text-sm text-gray-500">Prueba con otros criterios de búsqueda o <button @click="clearFilters" class="text-indigo-600 hover:text-indigo-800 font-medium">limpia los filtros</button>.</p>
        </div>
        
        <!-- Mensaje cuando no hay logs en absoluto -->
        <div v-else-if="!isLoading && (!logs || logs.length === 0)" class="text-center py-10">
          <DocumentTextIcon class="mx-auto h-12 w-12 text-gray-400" />
          <h3 class="mt-2 text-sm font-medium text-gray-900">No hay registros de cambios</h3>
          <p class="mt-1 text-sm text-gray-500">No se han encontrado modificaciones para este usuario.</p>
        </div>

        <!-- Paginación -->
        <div v-if="pagination && pagination.meta?.lastPage && pagination.meta.lastPage > 1" class="mt-6 flex justify-center">
          <nav class="flex items-center justify-between">
            <div class="flex-1 flex justify-between sm:hidden">
              <button 
                @click="() => { if (page > 1) { setPage(page - 1); refetch(); } }"
                :disabled="page <= 1"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                :class="{ 'opacity-50 cursor-not-allowed': page <= 1 }"
              >
                Anterior
              </button>
              <button
                @click="() => { if (pagination && pagination.meta?.lastPage && page < pagination.meta.lastPage) { setPage(page + 1); refetch(); } }"
                :disabled="!pagination || !pagination.meta?.lastPage || page >= (pagination.meta?.lastPage || 0)"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                :class="{ 'opacity-50 cursor-not-allowed': !pagination || !pagination.meta?.lastPage || page >= (pagination.meta?.lastPage || 0) }"
              >
                Siguiente
              </button>
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-center">
              <div>
                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <!-- Botón anterior -->
                  <button
                    @click="() => { if (page > 1) { setPage(page - 1); refetch(); } }"
                    :disabled="page <= 1"
                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    :class="{ 'opacity-50 cursor-not-allowed': page <= 1 }"
                  >
                    <span class="sr-only">Anterior</span>
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                  </button>
                  
                  <!-- Páginas -->
                  <template v-if="pagination && pagination.meta?.lastPage">
                    <template v-for="pageNum in pagination.meta.lastPage" :key="pageNum">
                      <button
                        v-if="pageNum === page || (pageNum <= 2 || (pagination && pagination.meta?.lastPage && pageNum >= pagination.meta.lastPage - 1) || Math.abs(pageNum - page) <= 1)"
                        @click="() => { setPage(pageNum); refetch(); }"
                        class="relative inline-flex items-center px-4 py-2 border text-sm font-medium"
                        :class="pageNum === page ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'"
                      >
                        {{ pageNum }}
                      </button>
                      <span
                        v-else-if="pageNum === 3 && page > 4 || (pagination && pagination.meta?.lastPage && pageNum === pagination.meta.lastPage - 2 && page < pagination.meta.lastPage - 3)"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                      >
                        ...
                      </span>
                    </template>
                  </template>
                  
                  <!-- Botón siguiente -->
                  <button
                    @click="() => { if (pagination && pagination.meta?.lastPage && page < pagination.meta.lastPage) { setPage(page + 1); refetch(); } }"
                    :disabled="!pagination || !pagination.meta?.lastPage || page >= (pagination.meta?.lastPage || 0)"
                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    :class="{ 'opacity-50 cursor-not-allowed': !pagination || !pagination.meta?.lastPage || page >= (pagination.meta?.lastPage || 0) }"
                  >
                    <span class="sr-only">Siguiente</span>
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </div>
  </div>

  <LoadingModal :when="isLoading" />
</template>
